import { format } from "date-fns";
import Handlebars from "handlebars";

import getArticleChannelData from "astrid-firestore/src/api/articles/getArticleChannelData";
import formatDate from "astrid-helpers/src/formatDate";

import getArticle from "./getArticle";

export function getDistributionDates(article, channel) {
	return {
		announcementDate: getArticleChannelData(article, channel.id, "announcementDate"),
		releaseDate: getArticleChannelData(article, channel.id, "releaseDate"),
		takedownDate: getArticleChannelData(article, channel.id, "takedownDate"),
	};
}

// Whether a "yyyy-MM-dd" date is after today in Stockholm. Not today on the machine's clock: the
// job runs on UTC, so a release queued at 01:00 Stockholm time would still be in the future.
export function compareDistributionDateWithToday(distributionDate, now = new Date()) {
	return !!distributionDate && distributionDate > formatDate(now);
}

function getFormatting(formatting, defaultFormatting) {
	// Check if formatting is actually provided or if it's the handlebars options object
	if (typeof formatting === "object" && formatting.hash) {
		return defaultFormatting;
	}
	return formatting;
}

Handlebars.registerHelper("getSentDateTime", () => {
	return formatDate().replaceAll("-", "");
});

Handlebars.registerHelper("tsToDate", (timestamp, formatting) => {
	if (!timestamp) {
		return "";
	}
	const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp._seconds * 1000);
	return format(date, getFormatting(formatting, "yyyyMMdd HH:mm:ss"));
});

Handlebars.registerHelper("hasDate", (article, channel, dateType) => {
	return !!getDistributionDates(article, channel)?.[dateType];
});

Handlebars.registerHelper("getDateByType", (article, channel, dateType, formatting) => {
	const date = getDistributionDates(article, channel)?.[dateType];
	if (!date) {
		return "";
	}

	return format(new Date(date), getFormatting(formatting, "yyyyMMdd"));
});

Handlebars.registerHelper("getDateByTypeExcel", (article, channel, dateType, formatting) => {
	const date = getDistributionDates(article, channel)?.[dateType];
	if (!date) {
		return "";
	}
	const format = getFormatting(formatting, "yyyy-MM-dd");
	const finalFormat = article.language === "pl" ? "yyyymmdd" : format; // polish date format
	return format(new Date(date), finalFormat);
});

Handlebars.registerHelper(
	"getDateByTypeWithLinkedArticle",
	(articleType, article, linkedArticle, channel, dateType) => {
		const art = getArticle({ articleType, article, linkedArticle });
		return getDistributionDates(art, channel)?.[dateType];
	},
);

Handlebars.registerHelper("getFullYear", () => {
	return new Date().getFullYear();
});

function toDate(value) {
	if (!value) {
		return null;
	}

	const date = value.toDate ? value.toDate() : new Date(value);

	return isNaN(date.getTime()) ? null : date;
}

function getOtherChannelIds(article, channel) {
	return (article?.channelIds || []).filter((channelId) => channelId !== channel?.id);
}

function getEarliestOtherChannelReleaseDate(article, channel) {
	const dates = getOtherChannelIds(article, channel)
		.map((channelId) => toDate(article?.channels?.[channelId]?.["releaseDate"]))
		.filter(Boolean)
		.map((date) => date.getTime());

	if (!dates.length) {
		return null;
	}

	return new Date(Math.min(...dates));
}

function getExclusivityWindow(article, channel) {
	const startDate = toDate(getDistributionDates(article, channel)?.releaseDate);
	const endDate = getEarliestOtherChannelReleaseDate(article, channel);

	if (!startDate || !endDate || startDate >= endDate) {
		return null;
	}

	const subtractedOneDayFromEndDate = new Date(endDate.getTime() - 24 * 60 * 60 * 1000);

	return { startDate, endDate: subtractedOneDayFromEndDate };
}

Handlebars.registerHelper("hasExclusivityWindow", (article, channel) => {
	return !!getExclusivityWindow(article, channel);
});

Handlebars.registerHelper("getExclusivityDate", (article, channel, dateType, formatting) => {
	const date = getExclusivityWindow(article, channel)?.[dateType];

	if (!date) {
		return "";
	}

	return format(date, getFormatting(formatting, "yyyyMMdd"));
});
